import { type PropsWithChildren } from 'react'
import type { Festive } from '@dhl-ui/pull-to-refresh'
import { PagePullToRefresh } from '@dhl-ui/pull-to-refresh'
import { RefreshProvider, usePageRefreshInternal } from './InnerPullToRefreshContext'

export type InnerPullToRefreshProps = PropsWithChildren<{
  /**
   * Optional season for the pull to refresh component
   */
  festive?: Festive

  /**
   * When you do not want customer to use pull to refresh, even when `onRefresh` has been
   * set. Or for example when you are in a flow on the page itself and you do not want to have its behavior.
   *
   * @default false
   */
  disablePullDown?: boolean

  /**
   * Give user option to refresh (partly) data/state of your page by using pull to refresh
   * on touch devices. When not giving, there is no pull to refresh possibility at all.
   * You can use `isRefreshing` and `isLoading` of React Query to react on these refreshings.
   * Or create your own solution arround this handler.
   *
   * Make sure you control what to do when refreshing, only update data or scroll etc.
   *
   */
  onRefresh?: () => Promise<unknown>
}>

const InnerComponent = ({ children, disablePullDown, festive, onRefresh }: InnerPullToRefreshProps) => {
  const { setIsPulling } = usePageRefreshInternal()

  const handleIsPulling = (arg: boolean) => {
    setIsPulling(arg)
  }

  return disablePullDown ? (
    children
  ) : (
    <PagePullToRefresh onRefresh={onRefresh} onPulling={handleIsPulling} festive={festive}>
      {children}
    </PagePullToRefresh>
  )
}

export const InnerPullToRefresh = (rest: InnerPullToRefreshProps) => (
  <RefreshProvider>
    <InnerComponent {...rest} />
  </RefreshProvider>
)
